import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import useForm from 'react-hook-form';
import * as yup from 'yup';
import { Helmet } from 'react-helmet';
import { Multiselect } from 'react-widgets';
import FormRichTextEditor from 'components/FormRichTextEditor/FormRichTextEditor';
import FormSelect from 'components/FormSelect/FormSelect';
import UserImg from '../UserImg/UserImg';
import Postcard from '../Postcard/Postcard';
import UploadImageButton from '../UploadImageButton/UploadImageButton';
import { getTopics } from 'redux/modules/topics';
import {
  create as createQuestion,
  feed as feedQuestions,
} from 'redux/modules/questions';
import { uploadFile } from 'redux/modules/s3';
import { searchToQuery } from 'utils/config';
import 'react-widgets/dist/css/react-widgets.css';
import { card } from '../Forum.module.scss';
import styles from './Post.module.scss';

const CustomMultiSelect = ({ name, setValue, register, disabled }) => {
  const [list, setList] = useState([]);
  const handleCreate = value => setList([...new Set([...list, value])]);
  const handleChange = values => setValue(name, values);
  useEffect(() => register({ name }), [register, name]);
  return (
    <div className={styles.select}>
      <label htmlFor={name}>Tags</label>
      <Multiselect
        name="tags"
        allowCreate={true}
        data={list}
        id={name}
        disabled={disabled}
        onCreate={handleCreate}
        onChange={handleChange}
      />
    </div>
  );
};

const FormComponent = ({ createQuestion, uploadFile, push, user, topics }) => {
  const onSubmit = data => {
    if (!!data?.images?.length) {
      const imagePromises = data.images.map(file =>
        file.file ? uploadFile(file.file) : file.url
      );
      return Promise.all(imagePromises).then(images => {
        return createQuestion({ ...data, images }).then(res =>
          push(`/forum/questions/${res.data._id}/${data.question}`)
        );
      });
    }
    return createQuestion(data).then(res =>
      push(`/forum/questions/${res.data._id}/${data.question}`)
    );
  };
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    errors,
    setError,
    clearError,
    getValues,
    formState,
  } = useForm({
    mode: 'onChange',
    validationSchema: yup.object().shape({
      question: yup
        .string()
        .required()
        .min(20),
      description: yup.string().required(),
      topic: yup.string().required(),
      tags: yup.array(yup.string()),
      // images: yup.array().of(
      //   yup.object().shape({
      //     file: yup.object().required(),
      //     url: yup.string().required(),
      //   })
      // ),
    }),
  });
  const question = watch('question');
  const questionValid = question && question.length >= 20;
  const topicList = useMemo(
    () =>
      Object.values(topics).map(topic => ({
        name: topic.name,
        value: topic.value,
      })),
    [topics]
  );
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.qnInput}>
        <UserImg />
        <input
          type="text"
          name="question"
          placeholder={
            !user
              ? 'Login to submit question'
              : 'What\'s your question or "aha" moment?'
          }
          ref={register}
          autoComplete="off"
          disabled={!user}
        />
        {question && !questionValid && (
          <div className={styles.min}>
            {20 - question.length} more letters to go
          </div>
        )}
      </div>
      <div
        className={cx(
          styles.desInput,
          questionValid && styles.display,
          formState.isSubmitting && styles.loading
        )}
      >
        <FormRichTextEditor
          name="description"
          placeholder="Description"
          {...{ setValue, errors, register }}
        />
        <div className={styles.select}>
          <FormSelect
            name="topic"
            label="Question Topic *"
            data={topicList}
            errors={errors}
            register={register}
          />
        </div>
      </div>
      <div className={cx(styles.tags, questionValid && styles.display)}>
        <CustomMultiSelect
          name="tags"
          {...{ setValue, register }}
          disabled={formState.isSubmitting}
        />
      </div>
      <div className={styles.action}>
        {questionValid && (
          <UploadImageButton
            name="images"
            {...{ register, setValue, setError, clearError, getValues, watch }}
            disabled={formState.isSubmitting}
          />
        )}
        <button
          type="submit"
          disabled={formState.isSubmitting || !formState.isValid}
        >
          POST
        </button>
        {errors.image && (
          <span className={styles.errors}>{errors.image.message}</span>
        )}
      </div>
    </form>
  );
};

FormComponent.propTypes = {
  createQuestion: PropTypes.func.isRequired,
  uploadFile: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  user: PropTypes.string,
};

const Form = connect(
  state => {
    const topics = getTopics(state);
    return { topics };
  },
  { createQuestion, uploadFile, push }
)(FormComponent);

const Post = ({
  user,
  questions,
  dataByPage,
  total,
  location,
  page,
  feedQuestions,
}) => {
  const [limit] = useState(100);
  useEffect(() => {
    feedQuestions({ limit, page });
  }, [user, feedQuestions, limit, page]);
  const data = (dataByPage && dataByPage[page]) || [];
  return (
    <div>
      <Helmet>
        <title>My Feed - Forums</title>
      </Helmet>
      <div className={cx(card, styles.post)}>
        <Form user={user} />
      </div>
      {data.map(questionId => {
        const question = questions[questionId];
        if (!question) return null;
        return <Postcard key={question._id} {...question} topic="home" />;
      })}
    </div>
  );
};

Post.propTypes = {
  user: PropTypes.string,
  questions: PropTypes.object.isRequired,
  dataByPage: PropTypes.object,
  total: PropTypes.number,
  location: PropTypes.object,
  page: PropTypes.number,
  feedQuestions: PropTypes.func.isRequired,
};

Post.defaultProps = { posts: [] };

export default connect(
  (state, ownProps) => {
    const {
      auth: { user },
      entities: { questions },
    } = state;

    const dataByPage = state.questions.idsByPage;
    const total = state.questions.total;
    const location = ownProps.location;
    const query = searchToQuery(location.search);
    const page = (query && query.page && parseInt(query.page, 10)) || 1;

    return {
      user,
      questions,
      dataByPage,
      total,
      location,
      page,
    };
  },
  { feedQuestions }
)(Post);
